import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import backendApi from '../shared/api/backendApi';

// Helper function to clean comma-separated values
const cleanCommaSeparatedValues = (valueString) => {
  if (!valueString || typeof valueString !== 'string') {
    return valueString;
  }
  
  // Split by comma and clean each value
  const values = valueString.split(',').map(v => v.trim());
  
  // Remove duplicates while preserving order
  const uniqueValues = [...new Set(values)];
  
  return uniqueValues.join(', ');
};

// Helper function to validate dealer data - REMOVED as per user request
// const validateDealerData = (dealer) => {
//   const issues = [];
//
//   // Check for excessive duplicates in invoice dates
//   const invoiceDates = dealer['Invoices Dates'];
//   if (invoiceDates && typeof invoiceDates === 'string') {
//     const dates = invoiceDates.split(',').map(d => d.trim());
//     const uniqueDates = [...new Set(dates)];
//     if (dates.length > uniqueDates.length * 2) { // More than 50% duplicates
//       issues.push('Excessive duplicate invoice dates detected');
//     }
//   }
//
//   // Check for excessive duplicates in amounts
//   const amounts = dealer['Amount for Invoices Dates'];
//   if (amounts && typeof amounts === 'string') {
//     const amountList = amounts.split(',').map(a => a.trim());
//     const uniqueAmounts = [...new Set(amountList)];
//     if (amountList.length > uniqueAmounts.length * 2) { // More than 50% duplicates
//       issues.push('Excessive duplicate invoice amounts detected');
//     }
//   }
//
//   return issues;
// };

function Dealers() {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentHistoryOpen, setPaymentHistoryOpen] = useState(false);
  const [selectedDealerForPayments, setSelectedDealerForPayments] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    location: '',
    gst: '',
    payments: [{amount: '', date: ''}], // Array of payment objects
  });

  useEffect(() => {
    loadDealers();
  }, []);

  const loadDealers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await backendApi.getDealers();
      setDealers(data);
    } catch (err) {
      setError('Failed to load dealers. Please try again.');
      console.error('Error loading dealers:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleOpenDialog = async (dealer = null) => {
    console.log('🔍 handleOpenDialog called with dealer:', dealer);
    if (dealer) {
      console.log('🔍 Editing dealer with ID:', dealer['Dealer ID']);
      setEditingDealer(dealer);

      // Load payment history for editing
      try {
        console.log('🔍 Fetching payments for dealer ID:', dealer['Dealer ID']);
        const result = await backendApi.getDealerPayments(dealer['Dealer ID']);
        console.log('🔍 Payment fetch result:', result);

        let payments = [{amount: '', date: ''}];
        if (result.success && result.payments && result.payments.length > 0) {
          payments = result.payments.map(p => ({
            amount: String(p.amount || ''),
            date: p.paymentDate ? new Date(p.paymentDate).toISOString().split('T')[0] : ''
          }));
          console.log('🔍 Loaded payments:', payments);
        } else {
          console.log('🔍 No payments found or unsuccessful response');
        }

        setFormData({
          name: dealer['Dealer Name'] || '',
          contact: dealer['Contact'] || '',
          location: dealer['Location'] || '',
          gst: dealer['GST No'] || '',
          payments: payments,
        });
        console.log('🔍 Form data set for editing');
      } catch (error) {
        console.error('❌ Error loading payment history:', error);
        // Fallback to empty payments
        setFormData({
          name: dealer['Dealer Name'] || '',
          contact: dealer['Contact'] || '',
          location: dealer['Location'] || '',
          gst: dealer['GST No'] || '',
          payments: [{amount: '', date: ''}],
        });
        console.log('🔍 Fallback form data set');
      }
    } else {
      console.log('🔍 Adding new dealer');
      setEditingDealer(null);
      setFormData({
        id: '',
        name: '',
        contact: '',
        location: '',
        gst: '',
        payments: [{amount: '', date: ''}],
      });
    }
    console.log('🔍 Setting dialog open to true');
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingDealer(null);
    setFormData({
      name: '',
      contact: '',
      location: '',
      gst: '',
      payments: [{amount: '', date: ''}],
    });
  };

  // Handle payment input changes
  const handlePaymentChange = (index, field, value) => {
    setFormData(prev => {
      const newPayments = [...prev.payments];
      newPayments[index] = { ...newPayments[index], [field]: value };
      return { ...prev, payments: newPayments };
    });
  };

  // Add new payment field
  const handleAddPayment = () => {
    setFormData(prev => ({
      ...prev,
      payments: [...prev.payments, {amount: '', date: ''}]
    }));
  };

  // Remove payment field
  const handleRemovePayment = (index) => {
    if (formData.payments.length > 1) {
      setFormData(prev => ({
        ...prev,
        payments: prev.payments.filter((_, i) => i !== index)
      }));
    }
  };

  // Calculate total payments
  const calculateTotalPayments = () => {
    return formData.payments.reduce((total, payment) => {
      const amount = parseFloat(payment.amount) || 0;
      return total + amount;
    }, 0);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);

      // Format payments as array of payment objects
      const validPayments = formData.payments
        .filter(payment => payment.amount.trim() !== '' && parseFloat(payment.amount) > 0)
        .map((payment, index) => ({
          amount: parseFloat(payment.amount),
          paymentDate: payment.date ? new Date(payment.date) : new Date(),
          paymentMode: 'cash',
          notes: `Payment ${index + 1}`
        }));

      const dataToSave = {
        name: formData.name,
        contact: formData.contact,
        location: formData.location,
        gst: formData.gst,
        payments: validPayments
      };

      if (editingDealer) {
        // Update existing dealer
        console.log('🔄 Updating existing dealer:', editingDealer['Dealer ID']);
        console.log('💰 Payments to save:', validPayments);
        await backendApi.updateDealer(editingDealer['Dealer ID'], dataToSave);
        console.log('✅ Dealer updated successfully');
      } else {
        // Add new dealer
        console.log('➕ Adding new dealer:', formData.name);
        console.log('💰 Payments to save:', validPayments);
        await backendApi.addDealer(dataToSave);
        console.log('✅ Dealer added successfully');
      }

      await loadDealers();
      handleCloseDialog();
    } catch (err) {
      setError('Failed to save dealer. Please try again.');
      console.error('Error saving dealer:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRefreshData = async () => {
    try {
      setLoading(true);
      await loadDealers();
      setLastRefresh(new Date());
    } catch (err) {
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async (dealerId) => {
    try {
      console.log(`🔍 Loading payment history for dealer ${dealerId}`);
      const result = await backendApi.getDealerPayments(dealerId);

      if (result.success) {
        setPaymentHistory(result.payments || []);
        setSelectedDealerForPayments(dealerId);
        setPaymentHistoryOpen(true);
        console.log(`✅ Loaded ${result.payments?.length || 0} payments for dealer ${dealerId}`);
      } else {
        console.log('❌ Failed to load payment history:', result.message);
        setError('Failed to load payment history');
        // Still show empty payment history dialog
        setPaymentHistory([]);
        setSelectedDealerForPayments(dealerId);
        setPaymentHistoryOpen(true);
      }
    } catch (err) {
      console.error('❌ Error loading payment history:', err);
      setError('Failed to load payment history');
      // Show empty payment history on error
      setPaymentHistory([]);
      setSelectedDealerForPayments(dealerId);
      setPaymentHistoryOpen(true);
    }
  };

  const handleClosePaymentHistory = () => {
    setPaymentHistoryOpen(false);
    setPaymentHistory([]);
    setSelectedDealerForPayments(null);
  };

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Dealer Management</Typography>
          <Box>
            <Button
              variant="outlined"
              onClick={handleRefreshData}
              disabled={loading}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={saving}
            >
              Add Dealer
            </Button>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Dealer Management</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={saving}
          >
            Add Dealer
          </Button>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {lastRefresh.toLocaleString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Showing {dealers.length} dealers
        </Typography>
      </Box>


      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2, maxHeight: '75vh' }} stickyHeader>
        <Table>
          <TableHead sx={{ backgroundColor: '#1e293b', position: 'sticky', top: 0, zIndex: 1 }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Dealer ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Dealer Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Contact</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Location</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>GST No</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Invoice Dates</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Invoice Amounts</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Purchase</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Paid</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Total Due</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dealers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No dealers yet. Add your first purchase to create dealers automatically.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : dealers.slice().reverse().map((dealer, index) => (
              <TableRow key={dealers.length - 1 - index}>
                <TableCell>{dealer['Dealer ID']}</TableCell>
                <TableCell>{dealer['Dealer Name']}</TableCell>
                <TableCell>{dealer['Contact']}</TableCell>
                <TableCell>{dealer['Location']}</TableCell>
                <TableCell>{dealer['GST No']}</TableCell>
                <TableCell>{dealer['Invoices Dates'] || 'None'}</TableCell>
                <TableCell>
                  {(() => {
                    const amount = dealer['Amount for Invoices Dates'];
                    if (!amount && amount !== 0) return 'None';

                    // Clean duplicate amounts
                    const cleanedAmount = cleanCommaSeparatedValues(String(amount));

                    // If it's a comma-separated string, display each amount formatted
                    if (typeof cleanedAmount === 'string' && cleanedAmount.includes(',')) {
                      const amounts = cleanedAmount.split(',').map(a => parseFloat(a.replace(/₹/g, '').trim()) || 0);
                      const formattedAmounts = amounts.map(a => `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(a)}`);
                      return formattedAmounts.join(', ');
                    }

                    const num = typeof cleanedAmount === 'number' ? cleanedAmount : parseFloat(String(cleanedAmount).replace(/,/g, '').replace(/₹/g, '')) || 0;
                    return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(num)}`;
                  })()}
                </TableCell>
                <TableCell>₹{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(dealer['Total Purchase Amount'] || 0)}</TableCell>
                <TableCell>
                  {(() => {
                    const totalPaid = dealer['Total Paid'] || 0;
                    if (typeof totalPaid === 'number') {
                      return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(totalPaid)}`;
                    } else {
                      const totalPaidString = String(totalPaid);
                      // Clean duplicate payments
                      const cleanedPayments = cleanCommaSeparatedValues(totalPaidString);
                      const payments = cleanedPayments.split(',').map(p => p.trim());
                      const total = payments.reduce((sum, payment) => {
                        const amount = parseFloat(payment.replace(/,/g, ''));
                        return sum + (isNaN(amount) ? 0 : amount);
                      }, 0);
                      return (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            ₹{new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(total)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({payments.length} payments)
                          </Typography>
                        </Box>
                      );
                    }
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const raw = dealer['Total Due'] ?? 0;
                    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, '').replace(/,/g, '')) || 0;
                    const color = num > 0 ? 'error.main' : (num < 0 ? 'success.main' : 'text.primary');
                    const formatted = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(num);
                    return (
                      <Typography sx={{ fontWeight: 800, color }}>
                        ₹{formatted}
                      </Typography>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(dealer)} disabled={saving}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dealer Dialog */}
      <Dialog open={open} onClose={saving ? undefined : handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDealer ? 'Edit Dealer' : 'Add New Dealer'}
          {saving && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Processing...
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Dealer Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            disabled={saving}
            required
          />
          <TextField
            margin="dense"
            label="Contact"
            fullWidth
            variant="outlined"
            value={formData.contact}
            onChange={(e) => handleInputChange('contact', e.target.value)}
            disabled={saving}
          />
          <TextField
            margin="dense"
            label="Location"
            fullWidth
            variant="outlined"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            disabled={saving}
          />
          <TextField
            margin="dense"
            label="GST Number"
            fullWidth
            variant="outlined"
            value={formData.gst}
            onChange={(e) => handleInputChange('gst', e.target.value)}
            disabled={saving}
          />
          {/* Multiple Payments Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Payment History
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add multiple payments made by this dealer over time
            </Typography>

            {formData.payments.map((payment, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <TextField
                  label={`Payment ${index + 1} Amount`}
                  type="number"
                  value={payment.amount}
                  onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)}
                  disabled={saving}
                  sx={{ flex: 1 }}
                  placeholder="Enter amount"
                />
                <TextField
                  label={`Payment ${index + 1} Date`}
                  type="date"
                  value={payment.date}
                  onChange={(e) => handlePaymentChange(index, 'date', e.target.value)}
                  disabled={saving}
                  sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                />
                {formData.payments.length > 1 && (
                  <IconButton
                    onClick={() => handleRemovePayment(index)}
                    disabled={saving}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}

            <Button
              onClick={handleAddPayment}
              disabled={saving}
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              sx={{ mt: 1 }}
            >
              Add Another Payment
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total Payments:
              </Typography>
              <Chip
                label={`₹${calculateTotalPayments()}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !formData.name.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Processing...' : (editingDealer ? 'Update Dealer' : 'Add Dealer')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dealers;
